import { useEffect, useState } from 'react';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';
import { useToast } from 'context/tostifyContext';
import { updateRecordModifiedDate } from 'routes/Pages/util/CommonAPIs';

function useManageRoles() {
  // State management
  const [allRoles, setAllRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [allButtons, setAllButtons] = useState([]);
  const [roleButtons, setRoleButtons] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedPermissions, setEditedPermissions] = useState([]);
  const [isEditForestMode, setIsEditForestMode] = useState(false);
  const [forestData, setForestData] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [assignedForests, setAssignedForests] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [selectedForestIds, setSelectedForestIds] = useState([]);

  // Add Role modal state
  const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [addRoleError, setAddRoleError] = useState('');

  const { showToast } = useToast();

  // Initial data fetch
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Update role-specific data when role changes
  useEffect(() => {
    if (selectedRole) {
      getRoleButtons(selectedRole.id);
      const roleForests = forestData.filter(f => f.role_Profile_ID === selectedRole.id);
      setAssignedForests(roleForests);
    } else {
      resetRoleData();
      resetEditStates();
    }
  }, [selectedRole, forestData]);

  // Fetch all initial data
  const fetchInitialData = () => {
    getRoles();
    getAllButtons();
    getAllForest();
    getAllContracts();
    getAllGeofences();
  };

  // API Calls
  const getRoles = async () => {
    try {
      const { data } = await customAxios.get('/User/GetAllRoles', { headers: authHeader() });
      setAllRoles(data || []);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const getAllButtons = async () => {
    try {
      const { data } = await customAxios.get('/User/GetAllButtons', { headers: authHeader() });
      setAllButtons(data || []);
    } catch (err) {
      console.error('Error fetching buttons:', err);
    }
  };

  const getRoleButtons = async roleId => {
    try {
      const { data } = await customAxios.get(`/User/GetRoleButtons?roleId=${roleId}`, {
        headers: authHeader(),
      });
      setRoleButtons(data || []);
    } catch (err) {
      console.error('Error fetching role buttons:', err);
      setRoleButtons([]);
    }
  };

  const getAllForest = async () => {
    try {
      const { data } = await customAxios.get('/Ops/GetGeofencingContractsPerProfile', {
        headers: authHeader(),
      });
      setForestData(data || []);
    } catch (err) {
      console.error('Error fetching forests:', err);
    }
  };

  const getAllGeofences = async () => {
    try {
      const { data } = await customAxios.get('/Ops/GetGeofencingData', {
        headers: authHeader(),
      });
      setGeofences(data || []);
    } catch (error) {
      console.log(error);
    }
  };

  const getAllContracts = async () => {
    try {
      const { data } = await customAxios.get('/Ops/GetContracts?flagActive=1', {
        headers: authHeader(),
      });
      setContracts(data || []);
    } catch (err) {
      console.error('Error fetching contracts:', err);
    }
  };

  // Helper functions
  const getUniqueForests = () => {
    const seen = new Set();
    return forestData.filter(forest => {
      if (seen.has(forest.siteAssessmentID)) {
        return false;
      }
      seen.add(forest.siteAssessmentID);
      return true;
    });
  };

  const resetRoleData = () => {
    setRoleButtons([]);
    setAssignedForests([]);
  };

  const resetEditStates = () => {
    setIsEditMode(false);
    setIsEditForestMode(false);
    setEditedPermissions([]);
  };

  // Event handlers
  const handleRoleChange = event => {
    const selectedId = event.target.value;
    const selected = allRoles.find(r => r.id === selectedId);
    setSelectedRole(selected);
  };

  const handleEditRole = () => {
    setIsEditMode(true);
    setEditedPermissions(roleButtons.map(rb => rb.buttonId));
  };

  const handleEditForest = () => {
    setIsEditForestMode(true);
    setSelectedForestIds([]);
  };

  const handleCheckboxChange = buttonId => {
    setEditedPermissions(prev => (prev.includes(buttonId) ? prev.filter(id => id !== buttonId) : [...prev, buttonId]));
  };

  const handleCancel = () => {
    resetEditStates();
    setSelectedForestIds([]);
    fetchInitialData();
  };

  const handleSave = async () => {
    if (isEditMode) {
      await saveRolePermissions();
      fetchInitialData();
    }

    if (isEditForestMode) {
      await saveForestAssignments();
    }
  };

  const saveRolePermissions = async () => {
    try {
      const payloads = editedPermissions.map(buttonId => ({
        roleId: selectedRole.id,
        buttonId,
      }));

      await customAxios.post('/User/DeleteRoleButton', { roleId: selectedRole.id }, { headers: authHeader() });

      await Promise.all(payloads.map(payload => customAxios.post('User/AddRoleButton', payload, { headers: authHeader() })));

      await getRoleButtons(selectedRole.id);
      showToast('Role permissions updated successfully.');
      setIsEditMode(false);
    } catch (err) {
      console.error('Error saving permissions:', err);
      showToast('Failed to update role permissions.', 'error');
    }
  };

  const saveForestAssignments = async () => {
    try {
      if (selectedForestIds.length === 0) {
        showToast('No forests selected to assign', 'warning');
        setIsEditForestMode(false);
        return;
      }

      const payloads = selectedForestIds
        .map(key => {
          const [siteAssessmentID, geofenceType] = key.split('_');

          const forest = geofences.find(
            g => g.siteAssessmentID.toString() === siteAssessmentID && g.geofenceType === geofenceType,
          );

          if (!forest) {
            console.error(`Forest not found for key: ${key}`);
            return null;
          }

          return {
            siteAssessmentID: forest.siteAssessmentID,
            contractUniqueID: forest.contractUniqueID,
            locationName: forest.wH_NumberName,
            roleProfileId: selectedRole?.id,
            geofenceType: forest.geofenceType,
          };
        })
        .filter(Boolean);

      if (payloads.length === 0) {
        showToast('No valid forests to assign', 'error');
        return;
      }

      await Promise.all(
        payloads.map(async payload => {
          await customAxios.post('/Ops/AddGeofencingContractsPerProfile', payload, { headers: authHeader() });

          await updateRecordModifiedDate(payload.siteAssessmentID);
        }),
      );

      showToast('Forest assignments saved successfully.');
      setSelectedForestIds([]);

      await getAllForest();
    } catch (err) {
      console.error('Error saving forest assignments:', err);
      showToast('Failed to save forest assignments.', 'error');
    }
  };

  const isButtonChecked = button => {
    if (isEditMode) {
      return editedPermissions.includes(button.buttonId);
    }
    return roleButtons.some(rb => rb.buttonId === button.buttonId);
  };

  const handleRemoveAssignedForest = async forest => {
    try {
      const payload = {
        siteAssessmentID: forest.siteAssessmentID,
        contractUniqueID: forest.contractUniqueID,
        roleProfileId: selectedRole.id,
      };

      await customAxios.post('/Ops/DeleteGeofencingContractsPerProfile', payload, { headers: authHeader() });

      await getAllForest();

      showToast('Forest removed successfully');
    } catch (err) {
      console.error('Error removing forest:', err);
      showToast('Failed to remove forest', 'error');
    }
  };

  // Add Role handlers
  const handleOpenAddRole = () => {
    setNewRoleName('');
    setAddRoleError('');
    setIsAddRoleModalOpen(true);
  };

  const handleCloseAddRole = () => {
    setIsAddRoleModalOpen(false);
    setNewRoleName('');
    setAddRoleError('');
  };

  const handleAddRole = async () => {
    const trimmed = newRoleName.trim();

    if (!trimmed) {
      setAddRoleError('Role name is required.');
      return;
    }

    // Check if role already exists (case-insensitive)
    const exists = allRoles.some(r => r.rolename?.toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      setAddRoleError('A role with this name already exists.');
      return;
    }

    // Auto-increment ID
    const maxId = allRoles.reduce((max, r) => (r.id > max ? r.id : max), 0);
    const newId = maxId + 1;

    try {
      await customAxios.post('/User/AddRoles', { id: newId, roleName: trimmed }, { headers: authHeader() });
      showToast('Role added successfully.');
      handleCloseAddRole();
      await getRoles();
    } catch (err) {
      console.error('Error adding role:', err);
      showToast('Failed to add role.', 'error');
    }
  };

  return {
    allRoles,
    selectedRole,
    allButtons,
    isEditMode,
    isEditForestMode,
    assignedForests,
    contracts,
    geofences,
    handleRoleChange,
    handleEditRole,
    handleEditForest,
    handleCheckboxChange,
    handleCancel,
    handleSave,
    isButtonChecked,
    getUniqueForests,
    selectedForestIds,
    setSelectedForestIds,
    handleRemoveAssignedForest,
    // Add Role
    isAddRoleModalOpen,
    newRoleName,
    setNewRoleName,
    addRoleError,
    handleOpenAddRole,
    handleCloseAddRole,
    handleAddRole,
  };
}

export default useManageRoles;
